import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import {
    Copy,
    Check,
    Save,
    Trash2,
    Eye,
    Edit3,
    Sparkles,
    Bug,
    Wrench,
    Shield,
    ArrowLeft,
    Download,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import ReleaseNotesImageUploader from '../ImageUploader'

// Convert HTML to Markdown
const htmlToMarkdown = (html: string): string => {
    return (
        html
            // Headers
            .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n')
            .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n')
            .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n')
            // Bold & Italic
            .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
            .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
            .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
            .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
            // Underline (no direct markdown, keep as is or use HTML)
            .replace(/<u[^>]*>(.*?)<\/u>/gi, '<u>$1</u>')
            // Code
            .replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`')
            // Links
            .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
            // Images
            .replace(
                /<img[^>]*src="([^"]*)"[^>]*alt="([^"]*)"[^>]*\/?>/gi,
                '![$2]($1)'
            )
            .replace(
                /<img[^>]*alt="([^"]*)"[^>]*src="([^"]*)"[^>]*\/?>/gi,
                '![$1]($2)'
            )
            .replace(/<img[^>]*src="([^"]*)"[^>]*\/?>/gi, '![]($1)')
            // Lists
            .replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_, content) => {
                return content.replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n')
            })
            .replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_, content) => {
                let index = 0
                return content.replace(/<li[^>]*>(.*?)<\/li>/gi, () => {
                    index++
                    return `${index}. $1\n`
                })
            })
            // Paragraphs & line breaks
            .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n')
            .replace(/<br\s*\/?>/gi, '\n')
            // Clean up remaining tags
            .replace(/<[^>]+>/g, '')
            // Clean up extra whitespace
            .replace(/\n{3,}/g, '\n\n')
            .trim()
    )
}

type VersionType = 'patch' | 'minor' | 'major'
type Category = 'feature' | 'bugfix' | 'improvement' | 'security'

interface ReleaseNoteDraft {
    versionType: VersionType
    category: Category
    title: string
    summary: string
    content: string // HTML content from editor
    lastSaved?: string
}

const DRAFT_KEY = 'release-note-draft'
const CURRENT_VERSION = __APP_VERSION__ || '4.0.8'

// Calculate next version based on type
const getNextVersion = (current: string, type: VersionType): string => {
    const parts = current.split('.').map(Number)
    if (parts.length !== 3) return current

    switch (type) {
        case 'major':
            return `${parts[0] + 1}.0.0`
        case 'minor':
            return `${parts[0]}.${parts[1] + 1}.0`
        case 'patch':
        default:
            return `${parts[0]}.${parts[1]}.${parts[2] + 1}`
    }
}

// Generate markdown from draft (converts HTML content to markdown)
const generateMarkdown = (draft: ReleaseNoteDraft, version: string): string => {
    const today = new Date().toISOString().split('T')[0]
    const markdownContent = htmlToMarkdown(draft.content)
    return `---
version: ${version}
date: ${today}
title: ${draft.title}
category: ${draft.category}
summary: ${draft.summary}
---

${markdownContent}
`
}

const ReleaseNotesEditor = () => {
    const { t } = useTranslation('release-notes')
    const navigate = useNavigate()
    const [copied, setCopied] = useState(false)

    const [draft, setDraft] = useState<ReleaseNoteDraft>({
        versionType: 'patch',
        category: 'feature',
        title: '',
        summary: '',
        content: '',
    })

    const nextVersion = getNextVersion(CURRENT_VERSION, draft.versionType)
    const markdown = generateMarkdown(draft, nextVersion)

    // Load draft from localStorage
    useEffect(() => {
        const saved = localStorage.getItem(DRAFT_KEY)
        if (saved) {
            try {
                const parsed = JSON.parse(saved)
                setDraft(parsed)
            } catch {
                // Invalid JSON, ignore
            }
        }
    }, [])

    // Auto-save draft
    useEffect(() => {
        const timer = setTimeout(() => {
            const toSave = { ...draft, lastSaved: new Date().toISOString() }
            localStorage.setItem(DRAFT_KEY, JSON.stringify(toSave))
        }, 1000)
        return () => clearTimeout(timer)
    }, [draft])

    const updateDraft = (field: keyof ReleaseNoteDraft, value: string) => {
        setDraft((prev) => ({ ...prev, [field]: value }))
    }

    const copyMarkdown = async () => {
        await navigator.clipboard.writeText(markdown)
        setCopied(true)
        toast.success(t('editor.copied-markdown'))
        setTimeout(() => setCopied(false), 2000)
    }

    const downloadMarkdown = () => {
        const blob = new Blob([markdown], { type: 'text/markdown' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `v${nextVersion}.md`
        a.click()
        URL.revokeObjectURL(url)
        toast.success(
            t('editor.downloaded', { filename: `v${nextVersion}.md` })
        )
    }

    const clearDraft = () => {
        if (confirm(t('editor.confirm-clear'))) {
            setDraft({
                versionType: 'patch',
                category: 'feature',
                title: '',
                summary: '',
                content: '',
            })
            localStorage.removeItem(DRAFT_KEY)
            toast.success(t('editor.draft-cleared'))
        }
    }

    const getCategoryIcon = (category: Category) => {
        const iconClass = 'h-4 w-4'
        switch (category) {
            case 'feature':
                return <Sparkles className={`${iconClass} text-emerald-500`} />
            case 'bugfix':
                return <Bug className={`${iconClass} text-red-500`} />
            case 'improvement':
                return <Wrench className={`${iconClass} text-blue-500`} />
            case 'security':
                return <Shield className={`${iconClass} text-orange-500`} />
        }
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/release-notes')}
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Edit3 className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold">
                                {t('editor.title')}
                            </h1>
                            <p className="text-sm text-muted-foreground">
                                {t('editor.description')}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="gap-1">
                            {t('editor.current-version')}: {CURRENT_VERSION}
                        </Badge>
                        <Badge className="gap-1 bg-primary">
                            {t('editor.next-version')}: {nextVersion}
                        </Badge>
                    </div>
                </div>
            </div>

            {/* Image Uploader */}
            <ReleaseNotesImageUploader />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Editor Panel */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <Edit3 className="h-4 w-4" />
                            {t('editor.form-title')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Version Type & Category */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>{t('editor.version-type')}</Label>
                                <Select
                                    value={draft.versionType}
                                    onValueChange={(v) =>
                                        updateDraft('versionType', v)
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="patch">
                                            Patch (
                                            {getNextVersion(
                                                CURRENT_VERSION,
                                                'patch'
                                            )}
                                            )
                                        </SelectItem>
                                        <SelectItem value="minor">
                                            Minor (
                                            {getNextVersion(
                                                CURRENT_VERSION,
                                                'minor'
                                            )}
                                            )
                                        </SelectItem>
                                        <SelectItem value="major">
                                            Major (
                                            {getNextVersion(
                                                CURRENT_VERSION,
                                                'major'
                                            )}
                                            )
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>{t('editor.category')}</Label>
                                <Select
                                    value={draft.category}
                                    onValueChange={(v) =>
                                        updateDraft('category', v)
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(
                                            [
                                                'feature',
                                                'improvement',
                                                'bugfix',
                                                'security',
                                            ] as const
                                        ).map((cat) => (
                                            <SelectItem key={cat} value={cat}>
                                                <span className="flex items-center gap-2">
                                                    {getCategoryIcon(cat)}
                                                    {t(`category.${cat}`)}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Title */}
                        <div className="space-y-2">
                            <Label>{t('editor.release-title')}</Label>
                            <Input
                                value={draft.title}
                                onChange={(e) =>
                                    updateDraft('title', e.target.value)
                                }
                                placeholder={t('editor.title-placeholder')}
                            />
                        </div>

                        {/* Summary */}
                        <div className="space-y-2">
                            <Label>{t('editor.summary')}</Label>
                            <Textarea
                                value={draft.summary}
                                onChange={(e) =>
                                    updateDraft('summary', e.target.value)
                                }
                                placeholder={t('editor.summary-placeholder')}
                                rows={2}
                            />
                        </div>

                        {/* Content */}
                        <div className="space-y-2">
                            <Label>{t('editor.content')}</Label>
                            <TextEditorCustomize
                                value={{
                                    text: draft.content,
                                    attachments: [],
                                }}
                                onChange={(val) =>
                                    updateDraft('content', val.text)
                                }
                                placeholder={t('editor.content-placeholder')}
                                imagePrefix="release-notes"
                                isPublic={true}
                                className="min-h-[300px]"
                            />
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={clearDraft}
                                className="text-destructive"
                            >
                                <Trash2 className="h-4 w-4 mr-1" />
                                {t('editor.clear-draft')}
                            </Button>

                            {draft.lastSaved && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Save className="h-3 w-3" />
                                    {t('editor.auto-saved')}
                                </span>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Preview Panel */}
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                                <Eye className="h-4 w-4" />
                                {t('editor.preview')}
                            </CardTitle>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={copyMarkdown}
                                    className="gap-1"
                                >
                                    {copied ? (
                                        <Check className="h-4 w-4" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                    {t('editor.copy-markdown')}
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={downloadMarkdown}
                                    className="gap-1"
                                >
                                    <Download className="h-4 w-4" />
                                    {t('editor.download')}
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="bg-muted rounded-lg p-4 overflow-auto max-h-[600px]">
                            <pre className="text-xs font-mono whitespace-pre-wrap">
                                {markdown}
                            </pre>
                        </div>

                        {/* Instructions */}
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <h4 className="font-medium text-sm text-blue-800 mb-2">
                                {t('editor.how-to-save')}
                            </h4>
                            <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                                <li>{t('editor.step-1')}</li>
                                <li>
                                    {t('editor.step-2', {
                                        filename: `v${nextVersion}.md`,
                                    })}
                                </li>
                                <li>{t('editor.step-3')}</li>
                            </ol>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}

export default ReleaseNotesEditor
